import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquareIcon, XIcon, SendIcon, ImageIcon, FileTextIcon, CalendarIcon, HomeIcon, MinimizeIcon, MaximizeIcon, UserIcon, PhoneIcon, MailIcon, TrendingUpIcon, MapPinIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, ExternalLinkIcon } from 'lucide-react';
interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  images?: string[];
  propertyCards?: PropertyCard[];
  documentStatus?: DocumentStatus;
  contactCard?: ContactCard;
  liveTracking?: LiveTracking;
}
interface PropertyCard {
  id: number;
  title: string;
  location: string;
  price: string;
  image: string;
  veriScore: number;
  bedrooms: number;
  bathrooms: number;
}
interface DocumentStatus {
  documents: {
    name: string;
    status: 'verified' | 'pending' | 'in-review';
    progress: number;
    lastUpdate: string;
  }[];
}
interface ContactCard {
  name: string;
  role: string;
  phone: string;
  email: string;
  image: string;
  available: boolean;
}
interface LiveTracking {
  status: string;
  progress: number;
  currentStep: string;
  estimatedCompletion: string;
  updates: {
    time: string;
    message: string;
    status: 'completed' | 'active' | 'pending';
  }[];
}
interface VeriBotWidgetProps {
  isOpen?: boolean;
  onToggle?: () => void;
}
const VeriBotWidget: React.FC<VeriBotWidgetProps> = ({
  isOpen: controlledIsOpen,
  onToggle
}) => {
  const [isOpen, setIsOpen] = useState(controlledIsOpen ?? false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    type: 'bot',
    content: "👋 Hi! I'm VeriBot, your AI property assistant. I can show you properties with images, track documents in real-time, connect you with agents, and provide investment insights. How can I help you today?",
    timestamp: new Date(),
    suggestions: ['Show properties in Lekki', 'Track my documents', 'Contact my agent', 'Calculate ROI']
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);
  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setIsOpen(!isOpen);
    }
  };
  const mockProperties: PropertyCard[] = [{
    id: 1,
    title: 'Luxury 3 Bedroom Apartment',
    location: 'Lekki Phase 1, Lagos',
    price: '₦75,000,000',
    image: 'https://images.unsplash.com/photo-1613545325278-f24b0cae1224?ixlib=rb-4.0.3&w=800&q=80',
    veriScore: 8.7,
    bedrooms: 3,
    bathrooms: 3
  }, {
    id: 2,
    title: 'Modern 4 Bedroom Duplex',
    location: 'Lekki Phase 1, Lagos',
    price: '₦120,000,000',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&w=800&q=80',
    veriScore: 9.1,
    bedrooms: 4,
    bathrooms: 4
  }, {
    id: 3,
    title: 'Waterfront Villa',
    location: 'Lekki Phase 1, Lagos',
    price: '₦350,000,000',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?ixlib=rb-4.0.3&w=800&q=80',
    veriScore: 9.5,
    bedrooms: 5,
    bathrooms: 5
  }];
  const simulateBotResponse = (userMessage: string): Message => {
    const lowerMessage = userMessage.toLowerCase();
    // Property search with images
    if (lowerMessage.includes('show') || lowerMessage.includes('properties') || lowerMessage.includes('lekki') || lowerMessage.includes('find')) {
      return {
        id: Date.now().toString(),
        type: 'bot',
        content: '🏠 I found 3 verified properties in Lekki Phase 1 that match your criteria. All have high VeriScores and excellent investment potential. Tap any property to see full details.',
        timestamp: new Date(),
        propertyCards: mockProperties,
        suggestions: ['Show more properties', 'Filter by price', 'Schedule viewings']
      };
    }
    // Live document tracking
    if (lowerMessage.includes('document') || lowerMessage.includes('track') || lowerMessage.includes('status') || lowerMessage.includes('verification')) {
      return {
        id: Date.now().toString(),
        type: 'bot',
        content: "📄 Here's your live document verification status. I'm monitoring all updates in real-time:",
        timestamp: new Date(),
        documentStatus: {
          documents: [{
            name: 'Certificate of Occupancy',
            status: 'verified',
            progress: 100,
            lastUpdate: '2 hours ago'
          }, {
            name: 'Survey Plan',
            status: 'verified',
            progress: 100,
            lastUpdate: '5 hours ago'
          }, {
            name: "Governor's Consent",
            status: 'in-review',
            progress: 75,
            lastUpdate: '30 minutes ago'
          }, {
            name: 'Deed of Assignment',
            status: 'verified',
            progress: 100,
            lastUpdate: '1 day ago'
          }, {
            name: 'Tax Clearance',
            status: 'pending',
            progress: 0,
            lastUpdate: 'Not started'
          }]
        },
        liveTracking: {
          status: 'In Progress',
          progress: 70,
          currentStep: "Governor's Consent Review",
          estimatedCompletion: 'May 18, 2023',
          updates: [{
            time: '30 min ago',
            message: "Governor's Consent submitted to state office",
            status: 'active'
          }, {
            time: '2 hours ago',
            message: 'Certificate of Occupancy verified successfully',
            status: 'completed'
          }, {
            time: '5 hours ago',
            message: 'Survey Plan approved by surveyor',
            status: 'completed'
          }, {
            time: 'Pending',
            message: 'Tax Clearance awaiting submission',
            status: 'pending'
          }]
        },
        suggestions: ['Contact legal team', 'Upload tax clearance', 'Get detailed report']
      };
    }
    // Contact agent/team
    if (lowerMessage.includes('contact') || lowerMessage.includes('agent') || lowerMessage.includes('call') || lowerMessage.includes('speak')) {
      return {
        id: Date.now().toString(),
        type: 'bot',
        content: "👤 Here's your dedicated property agent. They're available now and can assist you immediately:",
        timestamp: new Date(),
        contactCard: {
          name: 'Victoria Adeyemi',
          role: 'Senior Property Agent',
          phone: '+234 801 234 5678',
          email: 'victoria@veriprop.org',
          image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&w=400&q=80',
          available: true
        },
        suggestions: ['Call agent now', 'Send email', 'Schedule meeting', 'Contact legal team']
      };
    }
    // ROI and investment analysis
    if (lowerMessage.includes('roi') || lowerMessage.includes('investment') || lowerMessage.includes('calculate') || lowerMessage.includes('return')) {
      return {
        id: Date.now().toString(),
        type: 'bot',
        content: '📊 Investment Analysis for Lekki Phase 1:\n\n**VeriScore: 8.7/10** (Excellent)\n\n💰 Predicted Annual ROI: 18.5%\n📈 Property Appreciation: +12% (3-year)\n🏗️ Infrastructure Score: 9/10\n\n**Key Drivers:**\n• New Lekki-Epe Expressway (2024)\n• Eko Atlantic proximity\n• High rental demand (+18% YoY)\n• Shopping complex development\n\n**Recommendation:** Strong buy opportunity. Expected rental yield of 6.2% plus capital appreciation.',
        timestamp: new Date(),
        images: ['https://images.unsplash.com/photo-1460472178825-e5240623afd5?ixlib=rb-4.0.3&w=800&q=80'],
        suggestions: ['See comparable properties', 'Download full report', 'Schedule consultation']
      };
    }
    // Default response
    return {
      id: Date.now().toString(),
      type: 'bot',
      content: 'I can help you with:\n\n🏠 **Property Search** - Browse verified listings with images\n📄 **Live Document Tracking** - Real-time verification status\n👤 **Contact Management** - Connect with agents & legal teams\n📊 **Investment Analysis** - ROI calculations & VeriScores\n📅 **Service Scheduling** - Book cleanings, repairs, etc.\n\nWhat would you like to explore?',
      timestamp: new Date(),
      suggestions: ['Show properties', 'Track documents', 'Contact agent', 'Calculate ROI']
    };
  };
  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setTimeout(() => {
      const botResponse = simulateBotResponse(inputValue);
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };
  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    setTimeout(() => handleSendMessage(), 100);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  return <>
      {/* Floating Action Button with VeriBot Logo */}
      <AnimatePresence data-id="element-633">
        {!isOpen && <motion.button initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0,
        opacity: 0
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={handleToggle} className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-br from-[#FF4E00] via-[#FF6B2C] to-[#FF8C4D] text-white rounded-full shadow-2xl flex items-center justify-center group overflow-hidden" data-id="element-634">
            {/* Animated background pulse */}
            <motion.div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" animate={{
          scale: [1, 1.5, 1],
          opacity: [0.5, 0, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-635" />

            {/* VeriBot Logo */}
            <div className="relative z-10 flex flex-col items-center" data-id="element-636">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="group-hover:rotate-12 transition-transform" data-id="element-637">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="white" fillOpacity="0.9" data-id="element-638" />
                <path d="M2 17L12 22L22 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-639" />
                <path d="M2 12L12 17L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-640" />
                <circle cx="12" cy="7" r="2" fill="#FF4E00" data-id="element-641" />
              </svg>
            </div>

            {/* Active indicator */}
            <motion.div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white" animate={{
          scale: [1, 1.3, 1]
        }} transition={{
          repeat: Infinity,
          duration: 2
        }} data-id="element-642" />
          </motion.button>}
      </AnimatePresence>

      {/* Chat Widget */}
      <AnimatePresence data-id="element-643">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} className={`fixed z-50 bg-white rounded-2xl shadow-2xl overflow-hidden ${isMinimized ? 'bottom-6 right-6 w-80' : 'bottom-6 right-6 w-[440px] h-[680px]'}`} data-id="element-644">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#FF4E00] via-[#FF6B2C] to-[#FF8C4D] p-4 flex items-center justify-between relative overflow-hidden" data-id="element-645">
              {/* Animated background pattern */}
              <div className="absolute inset-0 opacity-10" data-id="element-646">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMCAwaDQwdjQwSDB6Ii8+PC9nPjwvZz48L3N2Zz4=')]" data-id="element-647"></div>
              </div>

              <div className="flex items-center space-x-3 relative z-10" data-id="element-648">
                <div className="relative" data-id="element-649">
                  <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-lg" data-id="element-650">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-651">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#FF4E00" fillOpacity="0.9" data-id="element-652" />
                      <path d="M2 17L12 22L22 17" stroke="#FF4E00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-653" />
                      <path d="M2 12L12 17L22 12" stroke="#FF4E00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-654" />
                      <circle cx="12" cy="7" r="1.5" fill="white" data-id="element-655" />
                    </svg>
                  </div>
                  <motion.div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white" animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                repeat: Infinity,
                duration: 2
              }} data-id="element-656" />
                </div>
                <div data-id="element-657">
                  <h3 className="font-bold text-white text-lg" data-id="element-658">VeriBot</h3>
                  <p className="text-xs text-white/90 flex items-center" data-id="element-659">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5" data-id="element-660"></span>
                    AI Assistant • Online
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2 relative z-10" data-id="element-661">
                <button onClick={() => setIsMinimized(!isMinimized)} className="p-2 hover:bg-white/20 rounded-lg transition" data-id="element-662">
                  {isMinimized ? <MaximizeIcon className="w-4 h-4 text-white" data-id="element-663" /> : <MinimizeIcon className="w-4 h-4 text-white" data-id="element-664" />}
                </button>
                <button onClick={handleToggle} className="p-2 hover:bg-white/20 rounded-lg transition" data-id="element-665">
                  <XIcon className="w-5 h-5 text-white" data-id="element-666" />
                </button>
              </div>
            </div>

            {!isMinimized && <>
                {/* Messages */}
                <div className="h-[520px] overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-667">
                  {messages.map((message, index) => <motion.div key={message.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-668">
                      <div className={`max-w-[90%] ${message.type === 'user' ? 'bg-gradient-to-br from-[#FF4E00] to-[#FF6B2C] text-white rounded-2xl rounded-tr-sm' : 'bg-white text-gray-800 rounded-2xl rounded-tl-sm shadow-md border border-gray-100'} px-4 py-3`} data-id="element-669">
                        <p className="text-sm whitespace-pre-line leading-relaxed" data-id="element-670">
                          {message.content}
                        </p>

                        {/* Property Cards */}
                        {message.propertyCards && <div className="mt-4 space-y-3" data-id="element-671">
                            {message.propertyCards.map(property => <motion.div key={property.id} whileHover={{
                    scale: 1.02
                  }} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200 cursor-pointer" data-id="element-672">
                                <img src={property.image} alt={property.title} className="w-full h-32 object-cover" data-id="element-673" />
                                <div className="p-3" data-id="element-674">
                                  <div className="flex items-start justify-between mb-2" data-id="element-675">
                                    <div className="flex-1" data-id="element-676">
                                      <h4 className="font-semibold text-sm text-gray-800" data-id="element-677">
                                        {property.title}
                                      </h4>
                                      <p className="text-xs text-gray-600 flex items-center mt-1" data-id="element-678">
                                        <MapPinIcon className="w-3 h-3 mr-1" data-id="element-679" />
                                        {property.location}
                                      </p>
                                    </div>
                                    <div className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-lg" data-id="element-680">
                                      {property.veriScore}/10
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between" data-id="element-681">
                                    <span className="text-[#FF4E00] font-bold text-sm" data-id="element-682">
                                      {property.price}
                                    </span>
                                    <div className="flex items-center space-x-3 text-xs text-gray-600" data-id="element-683">
                                      <span data-id="element-684">{property.bedrooms} beds</span>
                                      <span data-id="element-685">{property.bathrooms} baths</span>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>)}
                          </div>}

                        {/* Document Status */}
                        {message.documentStatus && <div className="mt-4 space-y-2" data-id="element-686">
                            {message.documentStatus.documents.map((doc, idx) => <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-id="element-687">
                                  <div className="flex items-center justify-between mb-2" data-id="element-688">
                                    <div className="flex items-center space-x-2" data-id="element-689">
                                      <FileTextIcon className="w-4 h-4 text-gray-600" data-id="element-690" />
                                      <span className="text-sm font-medium text-gray-800" data-id="element-691">
                                        {doc.name}
                                      </span>
                                    </div>
                                    {doc.status === 'verified' && <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-692" />}
                                    {doc.status === 'in-review' && <ClockIcon className="w-4 h-4 text-blue-600" data-id="element-693" />}
                                    {doc.status === 'pending' && <AlertCircleIcon className="w-4 h-4 text-gray-400" data-id="element-694" />}
                                  </div>
                                  <div className="flex items-center space-x-2" data-id="element-695">
                                    <div className="flex-1 bg-gray-200 rounded-full h-1.5" data-id="element-696">
                                      <motion.div initial={{
                          width: 0
                        }} animate={{
                          width: `${doc.progress}%`
                        }} transition={{
                          duration: 1,
                          delay: idx * 0.1
                        }} className={`h-1.5 rounded-full ${doc.status === 'verified' ? 'bg-green-600' : doc.status === 'in-review' ? 'bg-blue-600' : 'bg-gray-400'}`} data-id="element-697" />
                                    </div>
                                    <span className="text-xs text-gray-500" data-id="element-698">
                                      {doc.progress}%
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1" data-id="element-699">
                                    {doc.lastUpdate}
                                  </p>
                                </div>)}
                          </div>}

                        {/* Live Tracking */}
                        {message.liveTracking && <div className="mt-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200" data-id="element-700">
                            <div className="flex items-center justify-between mb-3" data-id="element-701">
                              <h4 className="font-semibold text-sm text-gray-800" data-id="element-702">
                                Live Progress
                              </h4>
                              <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full" data-id="element-703">
                                {message.liveTracking.progress}%
                              </span>
                            </div>
                            <div className="space-y-2 mb-3" data-id="element-704">
                              {message.liveTracking.updates.map((update, idx) => <div key={idx} className="flex items-start space-x-2" data-id="element-705">
                                    <div className={`w-2 h-2 rounded-full mt-1.5 ${update.status === 'completed' ? 'bg-green-500' : update.status === 'active' ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'}`} data-id="element-706" />
                                    <div className="flex-1" data-id="element-707">
                                      <p className="text-xs text-gray-700" data-id="element-708">
                                        {update.message}
                                      </p>
                                      <p className="text-xs text-gray-500" data-id="element-709">
                                        {update.time}
                                      </p>
                                    </div>
                                  </div>)}
                            </div>
                            <p className="text-xs text-gray-600" data-id="element-710">
                              Est. completion:{' '}
                              <span className="font-medium" data-id="element-711">
                                {message.liveTracking.estimatedCompletion}
                              </span>
                            </p>
                          </div>}

                        {/* Contact Card */}
                        {message.contactCard && <motion.div whileHover={{
                  scale: 1.02
                }} className="mt-4 bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-200" data-id="element-712">
                            <div className="flex items-center space-x-3 mb-3" data-id="element-713">
                              <div className="relative" data-id="element-714">
                                <img src={message.contactCard.image} alt={message.contactCard.name} className="w-12 h-12 rounded-full object-cover" data-id="element-715" />
                                {message.contactCard.available && <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" data-id="element-716" />}
                              </div>
                              <div className="flex-1" data-id="element-717">
                                <h4 className="font-semibold text-sm text-gray-800" data-id="element-718">
                                  {message.contactCard.name}
                                </h4>
                                <p className="text-xs text-gray-600" data-id="element-719">
                                  {message.contactCard.role}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-2" data-id="element-720">
                              <a href={`tel:${message.contactCard.phone}`} className="flex items-center space-x-2 text-xs text-gray-700 hover:text-[#FF4E00] transition" data-id="element-721">
                                <PhoneIcon className="w-3.5 h-3.5" data-id="element-722" />
                                <span data-id="element-723">{message.contactCard.phone}</span>
                              </a>
                              <a href={`mailto:${message.contactCard.email}`} className="flex items-center space-x-2 text-xs text-gray-700 hover:text-[#FF4E00] transition" data-id="element-724">
                                <MailIcon className="w-3.5 h-3.5" data-id="element-725" />
                                <span data-id="element-726">{message.contactCard.email}</span>
                              </a>
                            </div>
                          </motion.div>}

                        {/* Images */}
                        {message.images && <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-727">
                            {message.images.map((img, idx) => <img key={idx} src={img} alt="Property" className="w-full h-32 object-cover rounded-lg" data-id="element-728" />)}
                          </div>}

                        {/* Suggestions */}
                        {message.suggestions && <div className="mt-3 space-y-2" data-id="element-729">
                            {message.suggestions.map((suggestion, idx) => <motion.button key={idx} whileHover={{
                    scale: 1.02
                  }} whileTap={{
                    scale: 0.98
                  }} onClick={() => handleSuggestionClick(suggestion)} className="block w-full text-left text-xs px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition border border-gray-200 text-gray-700" data-id="element-730">
                                {suggestion}
                              </motion.button>)}
                          </div>}
                      </div>
                    </motion.div>)}

                  {isTyping && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex justify-start" data-id="element-731">
                      <div className="bg-white rounded-2xl rounded-tl-sm shadow-md px-4 py-3 border border-gray-100" data-id="element-732">
                        <div className="flex space-x-2" data-id="element-733">
                          <motion.div className="w-2 h-2 bg-[#FF4E00] rounded-full" animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 0.6,
                    delay: 0
                  }} data-id="element-734" />
                          <motion.div className="w-2 h-2 bg-[#FF4E00] rounded-full" animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 0.6,
                    delay: 0.2
                  }} data-id="element-735" />
                          <motion.div className="w-2 h-2 bg-[#FF4E00] rounded-full" animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 0.6,
                    delay: 0.4
                  }} data-id="element-736" />
                        </div>
                      </div>
                    </motion.div>}

                  <div ref={messagesEndRef} data-id="element-737" />
                </div>

                {/* Input */}
                <div className="p-4 bg-white border-t border-gray-200" data-id="element-738">
                  <div className="flex items-end space-x-2" data-id="element-739">
                    <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Ask me anything..." className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF4E00] focus:border-transparent text-sm" data-id="element-740" />
                    <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={handleSendMessage} disabled={!inputValue.trim()} className="p-3 bg-gradient-to-br from-[#FF4E00] to-[#FF6B2C] text-white rounded-xl hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-741">
                      <SendIcon className="w-5 h-5" data-id="element-742" />
                    </motion.button>
                  </div>
                </div>
              </>}

            {isMinimized && <div className="p-4" data-id="element-743">
                <p className="text-sm text-gray-600 flex items-center" data-id="element-744">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" data-id="element-745"></span>
                  VeriBot is ready to help
                </p>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </>;
};
export default VeriBotWidget;